import { useEffect } from "react";
import { RefreshCw, Video } from "lucide-react";
import { useBrandImage } from "../../lib/brand";
import { formatEventDate } from "../../lib/format";
import { useZoomMeeting } from "../../lib/meeting";
import { useTheatre } from "../site/SiteLayout";
import { StageControls } from "./StageControls";

function Welcome({ me, m }) {
  const art = useBrandImage("banner");
  const canJoin = me.embedded_enabled && me.zoom_configured;
  const { phase, message, details } = m.state;
  return (
    <section className="relative isolate overflow-hidden" data-testid="room-welcome">
      {art && <img src={art.src} srcSet={art.srcSet} sizes="100vw" alt="" aria-hidden="true" className="absolute inset-0 -z-10 h-full w-full object-cover opacity-35" />}
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-navy via-navy/85 to-navy/40" aria-hidden="true" />
      <div className="mx-auto max-w-5xl px-5 py-16 sm:py-24">
        <p className="eyebrow-gold mb-3">WELCOME TO THE EVENT ROOM</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl" data-testid="room-welcome-name">Welcome, {me.delegate.name}</h1>
        <p className="mt-4 max-w-2xl text-lg text-white/85">{me.event.name} · {formatEventDate(me.event)}</p>
        {!me.zoom_configured && <p className="mt-6 max-w-xl rounded bg-white/10 p-4 font-semibold" role="status" data-testid="zoom-not-configured">Zoom not yet configured. Please check back closer to the event.</p>}
        {phase === "left" && <p className="mt-6 text-white/85" role="status" data-testid="zoom-left">{message || "You have left the session."} You can rejoin while your verified session is active.</p>}
        {phase === "error" && (
          <div className="mt-6 max-w-xl rounded bg-burgundy/90 p-4" role="alert" data-testid="zoom-join-error">
            <p className="font-semibold">{message}</p>
            {details && <p className="mt-1 text-xs text-white/75" data-testid="zoom-join-error-details">Details: {details}</p>}
          </div>)}
        {canJoin && <button type="button" className="btn-primary mt-8 px-8 py-4 text-lg disabled:opacity-60" onClick={m.join} disabled={m.joining} data-testid="room-join-btn">
          {phase === "error" ? <RefreshCw size={20} aria-hidden="true" /> : <Video size={20} aria-hidden="true" />}
          {phase === "left" ? " Rejoin the session" : phase === "error" ? " Try again" : " Join the session"}</button>}
        <p className="mt-6 text-sm text-white/70">Please allow microphone and camera access when your browser asks. Use Theatre mode or Fullscreen once joined. Chat is in Zoom&apos;s toolbar.</p>
      </div>
    </section>
  );
}

// Before joining: welcome banner. From the Join click on: compact status/controls bar + the meeting centred in the main area.
export default function RoomMeeting({ me, onLive }) {
  const { theatre, setTheatre } = useTheatre();
  const m = useZoomMeeting(theatre);
  useEffect(() => { onLive(m.state.phase === "joined"); }, [m.state.phase, onLive]);
  const leave = async (msg) => { await m.leave(msg); setTheatre(false); };
  return (
    <div data-testid="room-meeting" data-phase={m.state.phase}>
      {!m.active && <Welcome me={me} m={m} />}
      <div ref={m.stage} className={`mx-auto w-full ${m.active ? "bg-[#0b1a2e] sm:w-[98vw] sm:pt-3" : ""}`} data-testid="meeting-stage">
        {m.active && <StageControls phase={m.state.phase} onLeave={leave} />}
        <div className="min-w-0"><div ref={m.root} id="meetingSDKElement" style={m.rootStyle} data-testid="zoom-embed-root" /></div>
      </div>
    </div>
  );
}
